import { parseJwt } from '../utils/jwtDecoder';
import { getItem } from '../utils/authStorage';
import { fetchWithAuthRetry, getAuthHeaders, ensureValidToken } from '../utils/helpers';

/**
 * Aerial Data (orthomosaic) service.
 * Endpoints are served by Django under /command-center/ortho/.
 */

function tenantId() {
  const token = getItem('accessToken');
  const payload = token ? parseJwt(token) : null;
  return payload?.tenant_id || '';
}

function jsonHeaders() {
  const tid = tenantId();
  return {
    ...getAuthHeaders(),
    'Content-Type': 'application/json',
    ...(tid ? { 'X-Tenant-Id': tid } : {}),
  };
}

function authOnlyHeaders() {
  const token = getItem('accessToken');
  const tid = tenantId();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tid ? { 'X-Tenant-Id': tid } : {}),
  };
}

async function parseError(response, prefix) {
  let detail = `${prefix} (HTTP ${response.status})`;
  try {
    const raw = await response.text();
    if (raw) {
      try {
        const j = JSON.parse(raw);
        detail = j.error || j.detail || j.message || raw;
      } catch {
        detail = raw;
      }
    }
  } catch {
    /* ignore */
  }
  return new Error(detail);
}

/** Upload an orthomosaic and create a slicing job. */
export async function uploadOrthoJob(file, { projectId, cropPx = 2048, name } = {}) {
  await ensureValidToken();
  const form = new FormData();
  form.append('file', file);
  if (projectId !== undefined && projectId !== null) form.append('project_id', String(projectId));
  form.append('crop_px', String(cropPx));
  if (name) form.append('name', name);

  const response = await fetchWithAuthRetry('/command-center/ortho/jobs/', {
    method: 'POST',
    headers: authOnlyHeaders(),
    body: form,
  });
  if (!response.ok) throw await parseError(response, 'Upload failed');
  return response.json();
}

/** List orthomosaic jobs for a project (album). */
export async function fetchOrthoJobs(projectId) {
  await ensureValidToken();
  const url = projectId
    ? `/command-center/ortho/jobs/?project_id=${encodeURIComponent(projectId)}`
    : '/command-center/ortho/jobs/';
  const response = await fetchWithAuthRetry(url, { headers: jsonHeaders() });
  if (!response.ok) throw await parseError(response, 'Failed to load aerial data');
  const json = await response.json();
  return Array.isArray(json) ? json : (json.results || []);
}

/** Retrieve a job with slices + detections (recovery payload). */
export async function fetchOrthoJob(jobId) {
  await ensureValidToken();
  const response = await fetchWithAuthRetry(`/command-center/ortho/jobs/${jobId}/`, { headers: jsonHeaders() });
  if (!response.ok) throw await parseError(response, 'Failed to load job');
  return response.json();
}

/** Trigger automated YOLO detection over all slices. */
export async function processOrthoJob(jobId) {
  await ensureValidToken();
  const response = await fetchWithAuthRetry(`/command-center/ortho/jobs/${jobId}/process/`, {
    method: 'POST',
    headers: jsonHeaders(),
  });
  if (!response.ok) throw await parseError(response, 'Failed to start processing');
  return response.json();
}

/** Delete an orthomosaic job and its files. */
export async function deleteOrthoJob(jobId) {
  await ensureValidToken();
  const response = await fetchWithAuthRetry(`/command-center/ortho/jobs/${jobId}/`, {
    method: 'DELETE',
    headers: authOnlyHeaders(),
  });
  if (!response.ok && response.status !== 204) {
    throw await parseError(response, 'Failed to delete aerial data');
  }
  return true;
}

/** List detections for a job. */
export async function fetchDetections(jobId) {
  await ensureValidToken();
  const response = await fetchWithAuthRetry(`/command-center/ortho/jobs/${jobId}/detections/`, { headers: jsonHeaders() });
  if (!response.ok) throw await parseError(response, 'Failed to load detections');
  return response.json();
}

/** Save manual boxes (Path A). detections: [{bbox:[x1,y1,x2,y2], class_name, slice_id?}] */
export async function saveManualDetections(jobId, detections) {
  await ensureValidToken();
  const response = await fetchWithAuthRetry(`/command-center/ortho/jobs/${jobId}/detections/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ detections }),
  });
  if (!response.ok) throw await parseError(response, 'Failed to save boxes');
  return response.json();
}

/** Adjust a detection's bbox or class_name. */
export async function updateDetection(detectionId, data) {
  await ensureValidToken();
  const response = await fetchWithAuthRetry(`/command-center/ortho/detections/${detectionId}/`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(data),
  });
  if (!response.ok) throw await parseError(response, 'Failed to update detection');
  return response.json();
}

/** Legacy wrapper for updateDetection. */
export async function updateDetectionBbox(detectionId, bbox) {
  return updateDetection(detectionId, { bbox });
}

/** Accept or reject a detection. action: 'accept' | 'reject' */
export async function reviewDetection(detectionId, action, reason = '') {
  await ensureValidToken();
  const response = await fetchWithAuthRetry(`/command-center/ortho/detections/${detectionId}/review/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ action, reason }),
  });
  if (!response.ok) throw await parseError(response, 'Failed to review detection');
  return response.json();
}

/** Mark job review as VERIFIED. */
export async function verifyOrthoJob(jobId) {
  await ensureValidToken();
  const response = await fetchWithAuthRetry(`/command-center/ortho/jobs/${jobId}/verify/`, {
    method: 'POST',
    headers: jsonHeaders(),
  });
  if (!response.ok) throw await parseError(response, 'Failed to verify job');
  return response.json();
}

/** Ingest accepted detections into site_data. */
export async function ingestOrthoJob(jobId) {
  await ensureValidToken();
  const response = await fetchWithAuthRetry(`/command-center/ortho/jobs/${jobId}/ingest/`, {
    method: 'POST',
    headers: jsonHeaders(),
  });
  if (!response.ok) throw await parseError(response, 'Failed to ingest detections into site_data');
  return response.json();
}

/** Reject an entire crop/image (all its machines). */
export async function rejectSliceImage(sliceId, reason = 'whole image false positive') {
  await ensureValidToken();
  const response = await fetchWithAuthRetry(`/command-center/ortho/slices/${sliceId}/reject/`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) throw await parseError(response, 'Failed to reject image');
  return response.json();
}

